/**
 * Google Analytics 4 服務
 * 處理所有分析追蹤功能
 */

import { logger } from '../utils/logger'

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    dataLayer?: any[]
  }
}

// 初始化 Google Analytics 4
export const initAnalytics = (measurementId: string) => {
  if (!measurementId) {
    // Google Analytics Measurement ID 未設置
    return
  }

  // 檢查是否已經初始化
  if (window.gtag && window.dataLayer && window.dataLayer.length > 0) {
    return
  }

  // 創建 dataLayer（與標準 GA4 代碼一致）
  window.dataLayer = window.dataLayer || []
  
  // 定義 gtag 函數（與標準 GA4 代碼一致）
  window.gtag = function() {
    window.dataLayer.push(arguments)
  }

  // 設置時間戳（與標準 GA4 代碼一致）
  window.gtag('js', new Date())

  // 加載 GA4 腳本（與標準 GA4 代碼一致）
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  // 配置 GA4（與標準 GA4 代碼一致）
  window.gtag('config', measurementId, {
    page_path: window.location.pathname,
    send_page_view: true,
  })

  // Google Analytics 4 初始化成功
}

// 追蹤頁面瀏覽
export const trackPageView = (path: string, title?: string) => {
  if (!window.gtag) return

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  })
}

// 追蹤自定義事件
export const trackEvent = (
  eventName: string,
  params?: Record<string, any>
) => {
  if (!window.gtag) return

  window.gtag('event', eventName, {
    ...params,
    timestamp: new Date().toISOString(),
  })
}

// 追蹤錢包連接
export const trackWalletConnect = (
  walletType: string,
  success: boolean,
  error?: string
) => {
  trackEvent('wallet_connect', {
    wallet_type: walletType,
    success,
    error: error || null,
  })
}

// 追蹤投資嘗試
export const trackInvestmentAttempt = (amountRange: string, hasReferrer: boolean) => {
  trackEvent('investment_attempt', {
    amount_range: amountRange,
    has_referrer: hasReferrer,
  })
}

// 追蹤投資成功
export const trackInvestmentSuccess = (amountRange: string, txHash: string) => {
  trackEvent('investment_success', {
    amount_range: amountRange,
    tx_hash: txHash,
  })
}

// 追蹤推薦連結點擊
export const trackReferralClick = (source: string) => {
  trackEvent('referral_click', {
    source, // 'twitter', 'telegram', 'direct', etc.
  })
}

// 追蹤推薦連結生成
export const trackReferralGenerate = () => {
  trackEvent('referral_generate', {})
}

// 追蹤語言切換
export const trackLanguageChange = (language: string) => {
  trackEvent('language_change', {
    language,
  })
}

// 獲取金額範圍（匿名化）
export const getAmountRange = (amount: number): string => {
  if (amount < 1000) return '<1k'
  if (amount < 5000) return '1k-5k'
  if (amount < 10000) return '5k-10k'
  if (amount < 50000) return '10k-50k'
  return '50k+'
}

// 檢查用戶是否同意 Cookie
export const getCookieConsent = (): 'all' | 'essential' | null => {
  if (typeof window === 'undefined') return null
  const consent = localStorage.getItem('cookie_consent')
  return consent as 'all' | 'essential' | null
}

// 設置 Cookie 同意
export const setCookieConsent = (consent: 'all' | 'essential') => {
  if (typeof window === 'undefined') return
  localStorage.setItem('cookie_consent', consent)
}

